import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ConsoleShell from '../../components/admin2/ConsoleShell';
import ManagedOpsPanel from '../../components/admin/ManagedOpsPanel';
import DistributePanel from './DistributePanel';
import InviteModal from './InviteModal';
import { fetchManagedOrderAdmin } from '../../lib/managedOrder';
import RequestFeePanel from '../../components/admin2/RequestFeePanel';
import { MANAGED_STAGES, stageKeyOf, isCancelled } from '../../lib/managedStages';
import { waTo } from '../../lib/maabarContact';

// One managed order in the new console — the concierge cockpit. Request summary +
// direct WhatsApp to the trader + a preview of the trader tracker, with the full
// lifecycle controls (ManagedOpsPanel) embedded below.
export default function ConsoleManagedDetail({ user, profile, lang }) {
  const isAr = lang === 'ar';
  const { id } = useParams();
  const nav = useNavigate();
  const [req, setReq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [distOpen, setDistOpen] = useState(false);
  const [invite, setInvite] = useState(null);   // { factoryId, request }
  const [toast, setToast] = useState('');

  const flash = (m) => { setToast(m); setTimeout(() => setToast(''), 1900); };

  const load = useCallback(async () => {
    setReq(await fetchManagedOrderAdmin(id));
    setLoading(false);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const title = req ? ((isAr ? (req.title_ar || req.title_en) : (req.title_en || req.title_ar)) || (isAr ? 'طلب' : 'Request')) : '';
  const trader = req?.requester?.company_name || req?.requester?.full_name || (isAr ? 'تاجر' : 'Trader');
  const phone = req?.contact_phone || req?.requester?.whatsapp;
  const stageK = stageKeyOf(req?.managed_status);
  const stageObj = MANAGED_STAGES.find((s) => s.key === stageK);
  const cancelled = isCancelled(req?.managed_status);

  return (
    <ConsoleShell user={user} profile={profile} lang={lang} active="managed">
      <div className="ac-page" style={{ fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
        <button className="ac-btn ac-btn-sm" onClick={() => nav('/admin2/managed')} style={{ marginBottom: 14 }}>
          {isAr ? '→ كل الطلبات المُدارة' : '← All managed orders'}
        </button>

        {loading ? (
          <div className="ac-skel" style={{ height: 120 }} />
        ) : !req ? (
          <div className="ac-placeholder">{isAr ? 'الطلب غير موجود.' : 'Order not found.'}</div>
        ) : (
          <>
            <div className="ac-page-head">
              <div>
                <h1 className="ac-h1">{title}</h1>
                <p className="ac-sub">
                  <span className="ac-badge neutral">{req.request_ref}</span>{' '}
                  {trader}
                  {req.quantity ? ` · ${isAr ? 'الكمية' : 'Qty'} ${req.quantity}${req.unit ? ' ' + req.unit : ''}` : ''}
                  {' · '}
                  <span style={{ color: cancelled ? 'var(--ac-danger, #c0392b)' : '#9A7B4F', fontWeight: 700 }}>
                    {cancelled ? (isAr ? 'ملغي' : 'Cancelled') : (stageObj ? (isAr ? stageObj.ar : stageObj.en) : req.managed_status)}
                  </span>
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
              {phone && (
                <a className="ac-btn ac-btn-sm" target="_blank" rel="noopener noreferrer"
                  href={waTo(phone, isAr ? `مرحباً، معك مدير حسابك في مَعبر بخصوص طلبك المُدار رقم ${req.request_ref || req.id}` : `Hello, this is your Maabar account manager regarding managed order ${req.request_ref || req.id}`)}
                  style={{ background: '#25D366', color: '#fff', textDecoration: 'none', borderColor: '#25D366' }}>
                  {isAr ? '💬 راسل التاجر على واتساب' : '💬 WhatsApp the trader'}
                </a>
              )}
              <a className="ac-btn ac-btn-sm" href={`/managed-order/${req.id}`} target="_blank" rel="noopener noreferrer">
                {isAr ? 'معاينة كما يراها التاجر ↗' : 'Preview as trader ↗'}
              </a>
              {/* Document generator, with this order already chosen. Linking
                  only — the tool never writes to the request or its offers. */}
              <button className="ac-btn ac-btn-sm" onClick={() => nav(`/admin2/docs/new?request=${req.id}&kind=quotation`)}>
                {isAr ? 'عرض سعر للعميل' : 'Client quotation'}
              </button>
              <button className="ac-btn ac-btn-sm" onClick={() => nav(`/admin2/docs/new?request=${req.id}&kind=rfq`)}>
                {isAr ? 'طلب تسعير للمصنع (RFQ)' : 'Supplier RFQ'}
              </button>
            </div>

            {/* The request fee. While it is unpaid the request is the trader's
                move, not ours: distribution, stage advances and the curated
                offer are all refused server-side (request_fee_settled). */}
            <RequestFeePanel fee={req.fee} requestId={req.id} isAr={isAr} onChanged={load} flash={flash} />

            {req.description && (
              <div className="ac-card" style={{ padding: 16, marginBottom: 14 }}>
                <div style={{ fontSize: 11, color: 'var(--ac-muted)', marginBottom: 6 }}>{isAr ? 'وصف الطلب' : 'Request brief'}</div>
                <div style={{ fontSize: 13.5, color: 'var(--ac-ink)', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{req.description}</div>
              </div>
            )}

            {/* Distribution, collapsed by default. A managed order reaches
                factories through the same invites as a quotation request, so it
                gets the same block rather than a second mechanism — but it is
                shut until asked for, because the concierge cockpit's job is the
                lifecycle below, not sourcing. */}
            <div className="ac-card" style={{ padding: 14, marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--ac-ink)' }}>
                    {isAr ? 'توزيع الطلب على المصانع' : 'Distribute to factories'}
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--ac-muted)', marginTop: 3 }}>
                    {isAr
                      ? 'أنشئ رابط دعوة لكل مصنع، ثم أرسله بنفسك.'
                      : 'Create an invite link per factory, then send it yourself.'}
                  </div>
                </div>
                <button className={`ac-btn ac-btn-sm${distOpen ? ' ac-btn-primary' : ''}`}
                  onClick={() => setDistOpen((v) => !v)}>
                  {distOpen ? (isAr ? 'إخفاء' : 'Hide') : (isAr ? 'فتح' : 'Open')}
                </button>
              </div>

              {distOpen && (
                <DistributePanel
                  requestId={id}
                  // ⚠️ BOTH of these were missing until 2026-09-20, and both are
                  // props that fail QUIETLY when absent.
                  //
                  // `request` feeds rfqProductValue() for {{2}} in the send
                  // preview (DistributePanel:587). Without it the admin reads a
                  // WhatsApp bubble with no product name and approves the send
                  // anyway, because nothing says the value is missing.
                  //
                  // `offerable` defaults to FALSE, so omitting it greys the
                  // whole panel and prints "هذا الطلب ملغي" on a live request.
                  // That is what this screen did to M-10075 in production.
                  // Computed in fetchManagedOrderAdmin off the RAW
                  // requests.status — never inferred here, and never read off
                  // req.managed_status, which is a DIFFERENT column with its own
                  // vocabulary and its own 'cancelled' value.
                  request={req}
                  // The raw requests.status, NOT req.managed_status — a
                  // different column with its own vocabulary and its own
                  // 'cancelled' value. Wording only.
                  rawStatus={req?.status}
                  offerable={!!req.offerable}
                  isAr={isAr}
                  flash={flash}
                  onOpenInvite={(factoryId) => setInvite({ factoryId, request: req })}
                />
              )}
            </div>

            <ManagedOpsPanel
              requestId={id} managedStatus={req.managed_status} isAr={isAr} onChanged={load}
              trackingNumber={req.tracking_number} shippingCompany={req.shipping_company} trackingUrl={req.tracking_url}
              // Named in the advance confirmation, so it says WHO gets the
              // message and with what reference — not just "a trader".
              traderName={trader} requestRef={req.request_ref}
            />
          </>
        )}
      </div>
      {invite && (
        <InviteModal factoryId={invite.factoryId} request={invite.request} isAr={isAr}
          onClose={() => setInvite(null)} flash={flash} />
      )}
      {toast && <div className="ac-toast" style={{ fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>{toast}</div>}
    </ConsoleShell>
  );
}
